import { useQuery } from "react-query";
import { loggedInApiCall } from "src/api/axiosClient/userApi";
import { MRT_SortingState } from "material-react-table";
import { mrtSortingStateToObject } from "src/api/apiHelpers";

import { z } from "zod";
import { zodParseWithLog } from "src/api/apiErrorLogs";

type Options = {
    page?: number;
    itemsPerPage?: number;
    search?: string;
    sorting?: MRT_SortingState;
};

type ParamsType = {
    params: {
        page: number;
        itemsPerPage: number;
        q: string;
        order?: Record<string, string>;
    };
};

export const useAllIcvs = (options?: Options) => {
    const { page = 1, itemsPerPage = 200, search = "", sorting = [] } = options || {};

    const params: ParamsType = {
        params: {
            page,
            itemsPerPage,
            q: search,
            order: mrtSortingStateToObject(sorting),
        },
    };

    const query = useQuery({
        queryKey: ["allIcvs", page, itemsPerPage, search, sorting],
        queryFn: async () => {
            return await loggedInApiCall.get("/api/icvs", params);
        },
        select: (request) => {
            const totalItems = request.data["hydra:totalItems"];
            const icvs = request.data["hydra:member"];

            const data = zodParseWithLog(icvs, IcvSchemaZod.array(), "allIcvs");

            return {
                items: data,
                totalItems: totalItems,
            };
        },
    });

    return {
        icvs: query.data?.items,
        totalItems: query.data?.totalItems,
        ...query,
    };
};

export type Icv = {
    "@id": IRI;
    id: number;
    name: string;
    code: string;
    description?: string;
    groupName?: string;
    unitCode: string;
    climate_change: number;
    ozone_depletion: number;
    ionising_radiation: number;
    photochemical_ozone_formation: number;
    particulate_matter: number;
    human_toxicity_non_cancer: number;
    human_toxicity_cancer: number;
    acidification: number;
    eutrophication_fresh_water: number;
    eutrophication_marine: number;
    eutrophication_terrestrial: number;
    ecotoxicity_freshwater: number;
    land_use: number;
    water_use: number;
    resource_use_fossils: number;
    resource_use_minerals_metals: number;
    climate_change_fossil: number;
    climate_change_biogenic: number;
    climate_change_land_use_lu_change: number;
    human_toxicity_non_cancer_organics: number;
    human_toxicity_non_cancer_inorganics: number;
    human_toxicity_non_cancer_metals: number;
    human_toxicity_cancer_organics: number;
    human_toxicity_cancer_inorganics: number;
    human_toxicity_cancer_metals: number;
    ecotoxicity_freshwater_organics: number;
    ecotoxicity_freshwater_inorganics: number;
    ecotoxicity_freshwater_metals: number;
};

export const IcvSchemaZod: z.ZodSchema<Icv> = z.object({
    "@id": z.string(),
    id: z.number(),
    name: z.string(),
    code: z.string(),
    description: z.string().optional(),
    groupName: z.string().optional(),
    unitCode: z.string(),
    climate_change: z.number(),
    ozone_depletion: z.number(),
    ionising_radiation: z.number(),
    photochemical_ozone_formation: z.number(),
    particulate_matter: z.number(),
    human_toxicity_non_cancer: z.number(),
    human_toxicity_cancer: z.number(),
    acidification: z.number(),
    eutrophication_fresh_water: z.number(),
    eutrophication_marine: z.number(),
    eutrophication_terrestrial: z.number(),
    ecotoxicity_freshwater: z.number(),
    land_use: z.number(),
    water_use: z.number(),
    resource_use_fossils: z.number(),
    resource_use_minerals_metals: z.number(),
    climate_change_fossil: z.number(),
    climate_change_biogenic: z.number(),
    climate_change_land_use_lu_change: z.number(),
    human_toxicity_non_cancer_organics: z.number(),
    human_toxicity_non_cancer_inorganics: z.number(),
    human_toxicity_non_cancer_metals: z.number(),
    human_toxicity_cancer_organics: z.number(),
    human_toxicity_cancer_inorganics: z.number(),
    human_toxicity_cancer_metals: z.number(),
    ecotoxicity_freshwater_organics: z.number(),
    ecotoxicity_freshwater_inorganics: z.number(),
    ecotoxicity_freshwater_metals: z.number(),
});
